import React from 'react';
import { SITE_CONFIG, FAQS } from './config';

// ============================================
// CONFIGURACIÓN DEL DOMINIO
// ============================================
// Cambiar esto cuando se despliegue al dominio final
export const DOMAIN = 'https://duettocatering.com';
export const SITE_NAME = 'Duetto Catering';

// ============================================
// SCHEMA MARKUP - LocalBusiness
// ============================================
const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "CateringService",
  "@id": `${DOMAIN}/#organization`,
  "name": SITE_NAME,
  "alternateName": "Duetto Catering Madrid",
  "description": "Servicio de catering para empresas en Madrid. Desayunos, coffee break, lunch y cócteles corporativos. Desde 6€/persona. +6 años de experiencia.",
  "url": DOMAIN,
  "logo": `${DOMAIN}/duetto-logo-negro.png`,
  "image": `${DOMAIN}/images/duetto/desayunos/desayunos-empresas.jpg`,
  "telephone": SITE_CONFIG.phone,
  "email": SITE_CONFIG.email,
  "foundingDate": "2018",
  "priceRange": "€€",
  "currenciesAccepted": "EUR",
  "paymentAccepted": "Transferencia bancaria, Tarjeta",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Madrid",
    "addressRegion": "Comunidad de Madrid",
    "addressCountry": "ES"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "40.4168",
    "longitude": "-3.7038"
  },
  "areaServed": {
    "@type": "City",
    "name": "Madrid",
    "@id": "https://www.wikidata.org/wiki/Q2807"
  },
  "serviceType": [
    "Catering para empresas",
    "Desayunos corporativos",
    "Coffee break",
    "Lunch empresarial",
    "Cócteles de empresa"
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Servicios de Catering",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Desayunos y Coffee Break",
          "description": "Café premium, bollería artesanal y opciones saludables"
        },
        "priceSpecification": {
          "@type": "PriceSpecification",
          "price": "6",
          "priceCurrency": "EUR",
          "unitText": "por persona"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Lunch y Buffet",
          "description": "Comidas informales para reuniones de trabajo"
        },
        "priceSpecification": {
          "@type": "PriceSpecification",
          "price": "15",
          "priceCurrency": "EUR",
          "unitText": "por persona"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Cócteles Corporativos",
          "description": "Servicio premium con camareros y barra libre"
        },
        "priceSpecification": {
          "@type": "PriceSpecification",
          "price": "29",
          "priceCurrency": "EUR",
          "unitText": "por persona"
        }
      }
    ]
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "bestRating": "5",
    "worstRating": "1",
    "ratingCount": "127"
  },
  "sameAs": [
    "https://www.instagram.com/duettocatering/",
    "https://www.linkedin.com/company/duetto-catering/"
  ],
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "09:00",
    "closes": "18:00"
  }
};

// ============================================
// SCHEMA MARKUP - FAQ
// ============================================
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

// ============================================
// SCHEMA MARKUP - WebSite (para búsqueda)
// ============================================
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": SITE_NAME,
  "url": DOMAIN,
  "description": "Catering para empresas en Madrid. Desayunos, lunch y cócteles corporativos.",
  "publisher": {
    "@id": `${DOMAIN}/#organization`
  }
};

// ============================================
// SCHEMA MARKUP - BreadcrumbList
// ============================================
const createBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url ? `${DOMAIN}${item.url}` : undefined
  }))
});

// ============================================
// CONFIGURACIÓN SEO POR PÁGINA
// ============================================
export const SEO_CONFIG = {
  home: {
    title: 'Catering para Empresas en Madrid | Duetto Catering',
    description: 'Servicio de catering corporativo en Madrid. Desayunos, coffee break, lunch y cócteles para eventos de empresa. Desde 6€/persona. Presupuesto sin compromiso.',
    keywords: 'catering empresas madrid, catering corporativo madrid, catering eventos madrid, catering oficinas madrid',
    canonical: '/web',
    breadcrumbs: [{ name: 'Inicio' }],
  },
  desayunos: {
    title: 'Desayunos para Empresas en Madrid | Coffee Break | Duetto Catering',
    description: 'Desayunos y coffee break para empresas en Madrid desde 6€/persona. Café premium, bollería artesanal y opciones saludables. Transporte incluido.',
    keywords: 'desayunos empresas madrid, coffee break madrid, desayunos corporativos, catering desayunos',
    canonical: '/web/desayunos',
    breadcrumbs: [
      { name: 'Inicio', url: '/web' },
      { name: 'Desayunos' }
    ],
  },
  lunch: {
    title: 'Lunch y Buffet para Empresas en Madrid | Duetto Catering',
    description: 'Lunch y buffet para reuniones de empresa en Madrid desde 15€/persona. Menús variados con opciones para todos los gustos. Presupuesto gratis.',
    keywords: 'lunch empresas madrid, buffet corporativo madrid, comida empresas, catering lunch madrid',
    canonical: '/web/lunch',
    breadcrumbs: [
      { name: 'Inicio', url: '/web' },
      { name: 'Lunch' }
    ],
  },
  cocteles: {
    title: 'Cócteles para Empresas en Madrid | Eventos Corporativos | Duetto Catering',
    description: 'Cócteles para eventos de empresa en Madrid desde 29€/persona. Camareros profesionales, barra libre y menaje incluido. Solicita presupuesto.',
    keywords: 'coctel empresas madrid, catering coctel madrid, eventos corporativos madrid, coctel corporativo',
    canonical: '/web/cocteles',
    breadcrumbs: [
      { name: 'Inicio', url: '/web' },
      { name: 'Cócteles' }
    ],
  },
  blog: {
    title: 'Blog sobre Catering para Empresas | Consejos y Tendencias | Duetto Catering',
    description: 'Artículos y consejos sobre catering corporativo, tendencias gastronómicas y cómo organizar eventos de empresa exitosos en Madrid.',
    keywords: 'blog catering, consejos eventos empresa, tendencias catering corporativo',
    canonical: '/web/blog',
    breadcrumbs: [
      { name: 'Inicio', url: '/web' },
      { name: 'Blog' }
    ],
  },
  nosotros: {
    title: 'Sobre Nosotros | Duetto Catering Madrid | Desde 2018',
    description: 'Conoce la historia de Duetto Catering. Desde 2018 sirviendo a empresas en Madrid. eBay, Repsol, TikTok y más de 500 eventos realizados.',
    keywords: 'duetto catering, catering madrid, empresa catering, historia duetto',
    canonical: '/web/nosotros',
    breadcrumbs: [
      { name: 'Inicio', url: '/web' },
      { name: 'Sobre Nosotros' }
    ],
  },
  contacto: {
    title: 'Contacto y Presupuesto | Catering Empresas Madrid | Duetto Catering',
    description: 'Solicita presupuesto gratis para tu evento de empresa. Respuesta en 24h. Llámanos al 91 572 24 41 o rellena el formulario.',
    keywords: 'presupuesto catering madrid, contacto catering empresas, contratar catering madrid',
    canonical: '/web/contacto',
    breadcrumbs: [
      { name: 'Inicio', url: '/web' },
      { name: 'Contacto' }
    ],
  },
};

// ============================================
// COMPONENTE SEO HEAD
// ============================================
export const SEOHead = ({ 
  page = 'home',
  customTitle,
  customDescription,
  customImage,
  article = false,
  noindex = false,
}) => {
  const config = SEO_CONFIG[page] || SEO_CONFIG.home;
  
  const title = customTitle || config.title;
  const description = customDescription || config.description;
  const canonical = `${DOMAIN}${config.canonical}`;
  const image = customImage || `${DOMAIN}/images/duetto/desayunos/desayunos-empresas.jpg`;
  
  // Schemas a incluir
  const schemas = [localBusinessSchema, websiteSchema];
  
  // Añadir FAQ schema solo en home
  if (page === 'home') {
    schemas.push(faqSchema);
  }
  
  // Añadir breadcrumbs
  if (config.breadcrumbs) {
    schemas.push(createBreadcrumbSchema(config.breadcrumbs));
  }

  // Efecto para manipular todos los SEO tags directamente
  React.useEffect(() => {
    // Establecer título
    document.title = title;
    
    // Establecer idioma
    document.documentElement.lang = 'es';

    // Helper para crear/actualizar meta tags
    const setMeta = (attr, attrValue, content) => {
      let meta = document.querySelector(`meta[${attr}="${attrValue}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, attrValue);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
      meta.setAttribute('data-seo', 'true');
    };

    // Helper para crear/actualizar link tags
    const setLink = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
      link.setAttribute('data-seo', 'true');
    };

    // Meta tags básicos
    setMeta('name', 'description', description);
    setMeta('name', 'keywords', config.keywords);
    setMeta('name', 'author', SITE_NAME);
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    
    // Geo tags
    setMeta('name', 'geo.region', 'ES-MD');
    setMeta('name', 'geo.placename', 'Madrid');
    setMeta('http-equiv', 'content-language', 'es-ES');
    
    // Open Graph
    setMeta('property', 'og:type', article ? 'article' : 'website');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:image', image);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:locale', 'es_ES');
    
    // Twitter Cards
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', image);
    
    // Canonical link
    setLink('canonical', canonical);

    // JSON-LD Schema
    const existingScripts = document.querySelectorAll('script[data-seo-jsonld]');
    existingScripts.forEach(script => script.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify(schemas);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [page, title, description, config.keywords, canonical, image, article, noindex, schemas]);

  // Este componente no renderiza nada visible
  return null;
};

// ============================================
// COMPONENTE PARA ARTÍCULOS DE BLOG
// ============================================
export const BlogSEOHead = ({ post }) => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "image": `${DOMAIN}${post.image}`,
    "datePublished": post.date,
    "dateModified": post.date,
    "author": {
      "@type": "Organization",
      "name": SITE_NAME,
      "@id": `${DOMAIN}/#organization`
    },
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "logo": {
        "@type": "ImageObject",
        "url": `${DOMAIN}/duetto-logo-negro.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${DOMAIN}/web/blog/${post.slug}`
    }
  };

  const title = `${post.title} | Blog Duetto Catering`;
  const canonical = `${DOMAIN}/web/blog/${post.slug}`;
  const image = `${DOMAIN}${post.image}`;

  // Efecto para manipular todos los SEO tags
  React.useEffect(() => {
    document.title = title;
    document.documentElement.lang = 'es';

    const setMeta = (attr, attrValue, content) => {
      let meta = document.querySelector(`meta[${attr}="${attrValue}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, attrValue);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
      meta.setAttribute('data-seo', 'true');
    };

    const setLink = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
      link.setAttribute('data-seo', 'true');
    };

    setMeta('name', 'description', post.excerpt);
    setMeta('name', 'author', SITE_NAME);
    setMeta('name', 'robots', 'index, follow');
    
    setMeta('property', 'og:type', 'article');
    setMeta('property', 'og:title', post.title);
    setMeta('property', 'og:description', post.excerpt);
    setMeta('property', 'og:image', image);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'article:published_time', post.date);
    setMeta('property', 'article:section', post.category);
    
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', post.title);
    setMeta('name', 'twitter:description', post.excerpt);
    setMeta('name', 'twitter:image', image);
    
    setLink('canonical', canonical);

    // JSON-LD
    const existingScripts = document.querySelectorAll('script[data-seo-jsonld]');
    existingScripts.forEach(script => script.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify([articleSchema, localBusinessSchema]);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [post.slug, post.title, post.excerpt, post.date, post.category, title, canonical, image, articleSchema]);

  return null;
};

export default SEOHead;
